import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, fetchApi } from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import { clearReviewDraft, loadReviewDraft, saveReviewDraft } from '@/lib/review-draft';
import { useReviewImageUpload } from '@/lib/reviews';

// Eden Treaty generates an irresolvable union for /reviews/:itemUin/media vs /reviews/:id
// because both occupy the same dynamic segment position. Use fetch for those 3 routes.
async function fetchReviewsApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `/api/reviews${path}`;
  const method = init?.method ?? 'GET';

  const res = await fetch(url, { ...init, credentials: 'include' });
  const text = await res.text();
  let json: { success?: boolean; data?: T; error?: { message?: string } } | null = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // Response body is not valid JSON
  }

  if (!res.ok || !json?.success) {
    console.error(`[reviews:api] ${method} ${url} failed`, {
      status: res.status,
      statusText: res.statusText,
      bodyEmpty: !text,
      bodyLength: text.length,
      response: json,
    });
    throw new Error(
      json?.error?.message ??
        `Request failed: ${res.status} (${text ? 'see response above' : 'empty body'})`,
    );
  }
  return json.data as T;
}

import DeleteConfirmationDialog from './delete-confirmation-dialog';
import ImageLightbox from './image-lightbox';
import ReviewCard from './review-card';
import ReviewForm from './review-form';
// Components
import ReviewHeader from './review-header';
import ReviewsEmptyState from './reviews-empty-state';

interface Review {
  id: string;
  itemUin: number;
  rating: number;
  title: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  userName: string;
  media: ReviewMedia[];
  itemName?: string;
}

interface ReviewMedia {
  id: string;
  userId: string;
  filename: string;
  s3Key: string;
  itemUin: number;
  reviewId: string | null;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
}

interface ReviewsSectionProps {
  itemUin: number;
  itemName: string;
  initialReviews: Review[];
}

export default function ReviewsSection({ itemUin, itemName, initialReviews }: ReviewsSectionProps) {
  const { data: sessionData } = useSession();
  const user = sessionData?.user ?? null;
  const isAuthenticated = !!sessionData;
  const navigate = useNavigate();
  const imageUpload = useReviewImageUpload(itemUin);

  // State management
  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const [hasDraft, setHasDraft] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Dialog states
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    rating: 5,
    content: '',
  });

  const [errors, setErrors] = useState({
    rating: '',
    content: '',
    images: '',
  });

  // Draft management - track if closing via cancel/submit (to prevent saving draft)
  const shouldClearDraftRef = useRef(false);

  // Check for draft existence on mount and when user changes
  useEffect(() => {
    const checkDraft = () => {
      const draft = loadReviewDraft(itemUin);
      setHasDraft(draft !== null);
    };
    checkDraft();
  }, [itemUin]);

  // Helper functions
  const hasUserReviewed = (): boolean => {
    if (!user) return false;
    return reviews.some((review) => review.userId === user.id);
  };

  const canEditReview = (review: Review): boolean => {
    return user?.id === review.userId;
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors = {
      rating: '',
      content: '',
      images: '',
    };

    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'דירוג חייב להיות בין 1 ל-5';
      setErrors(newErrors);
      return false;
    }

    if (formData.content.length < 10) {
      newErrors.content = 'התוכן קצר מדי (לפחות 10 תווים)';
      setErrors(newErrors);
      return false;
    }

    if (formData.content.length > 1000) {
      newErrors.content = 'התוכן ארוך מדי (מקסימום 1000 תווים)';
      setErrors(newErrors);
      return false;
    }

    setErrors(newErrors);
    return true;
  };

  const handleDeleteUploadedImage = async (mediaId: string): Promise<void> => {
    await imageUpload.handleDeleteUploadedImage(mediaId);
    if (editingReview) {
      setReviews((prev) =>
        prev.map((review) =>
          review.id === editingReview.id
            ? { ...review, media: review.media.filter((m) => m.id !== mediaId) }
            : review,
        ),
      );
    }
  };

  // Review CRUD operations
  const handleAddReview = async (): Promise<void> => {
    setEditingReview(null);

    // Try to load draft silently
    const draft = loadReviewDraft(itemUin);
    if (draft) {
      setFormData({ rating: draft.rating, content: draft.content });
    } else {
      setFormData({ rating: 5, content: '' });
    }

    // Fetch orphaned media (images uploaded previously but not attached to a review)
    try {
      const data = await fetchApi(
        api.api.items({ uin: String(itemUin) }).reviews['media-draft'].get(),
      );
      if (Array.isArray(data)) {
        imageUpload.initForEdit(
          data.map((m) => ({
            id: m.id,
            userId: m.userId,
            filename: m.filename,
            s3Key: m.s3Key,
            itemUin: m.itemUin,
            reviewId: m.reviewId,
            mimeType: m.mimeType,
            size: m.size,
            deletedAt: m.deletedAt,
            createdAt: String(m.createdAt),
            updatedAt: String(m.updatedAt),
          })),
        );
      } else {
        imageUpload.initForEdit([]);
      }
    } catch {
      imageUpload.initForEdit([]);
    }

    setErrors({ rating: '', content: '', images: '' });
    shouldClearDraftRef.current = false;
    setShowReviewDialog(true);
  };

  const handleEditReview = (review: Review): void => {
    setEditingReview(review);

    // Try to load draft silently (for edits, use reviewId)
    const draft = loadReviewDraft(itemUin, review.id);
    if (draft) {
      setFormData({ rating: draft.rating, content: draft.content });
    } else {
      setFormData({
        rating: review.rating,
        content: review.content,
      });
    }

    // Media is now returned as ReviewMedia[] from the API
    imageUpload.initForEdit(
      Array.isArray(review.media)
        ? review.media
            .filter((m): m is ReviewMedia => typeof m === 'object' && 'id' in m)
            .map((m) => ({
              ...m,
              size: null,
              deletedAt: null,
            }))
        : [],
    );
    setErrors({ rating: '', content: '', images: '' });
    shouldClearDraftRef.current = false;
    setShowReviewDialog(true);
  };

  const handleDeleteReview = (review: Review): void => {
    setReviewToDelete(review);
    setShowDeleteDialog(true);
  };

  const confirmDeleteReview = async (): Promise<void> => {
    if (!reviewToDelete) return;

    setDeleting(true);
    try {
      await fetchReviewsApi<{ deleted: boolean }>(`/${reviewToDelete.id}`, { method: 'DELETE' });

      setReviews((prev) => prev.filter((review) => review.id !== reviewToDelete.id));
      toast.success('הביקורת נמחקה בהצלחה');
      setShowDeleteDialog(false);
      setReviewToDelete(null);
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('שגיאה במחיקת הביקורת');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmitReview = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (imageUpload.uploadingFiles.size > 0) {
      toast.error('נא להמתין לסיום העלאת התמונות');
      return;
    }

    setSubmitting(true);

    try {
      if (editingReview) {
        await fetchReviewsApi<Record<string, unknown>>(`/${editingReview.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rating: formData.rating,
            content: formData.content,
            mediaIds: imageUpload.uploadedImages
              .filter((img) => !editingReview.media.some((m) => m.id === img.id))
              .map((img) => img.id),
          }),
        });

        setReviews((prev) =>
          prev.map((review) =>
            review.id === editingReview.id
              ? {
                  ...review,
                  rating: formData.rating,
                  content: formData.content,
                  updatedAt: new Date().toISOString(),
                }
              : review,
          ),
        );
        toast.success('הביקורת עודכנה בהצלחה');
      } else {
        const created = await fetchReviewsApi<{ id: string; createdAt: string; updatedAt: string }>(
          '',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              itemUin,
              rating: formData.rating,
              content: formData.content,
              mediaIds: imageUpload.uploadedImages.map((img) => img.id),
            }),
          },
        );

        setReviews((prev) => [
          {
            id: created.id,
            itemUin,
            rating: formData.rating,
            title: null,
            content: formData.content,
            createdAt: String(created.createdAt),
            updatedAt: String(created.updatedAt),
            userId: user?.id ?? '',
            userName: user?.name ?? '',
            media: imageUpload.uploadedImages.map((img) => ({
              id: img.id,
              userId: img.userId,
              filename: img.filename,
              s3Key: img.s3Key,
              itemUin: img.itemUin,
              reviewId: img.reviewId,
              mimeType: img.mimeType,
              createdAt: String(img.createdAt),
              updatedAt: String(img.updatedAt),
            })),
          },
          ...prev,
        ]);
        toast.success('הביקורת נוספה בהצלחה');
      }

      // Clear draft on successful submit
      shouldClearDraftRef.current = true;
      clearReviewDraft(itemUin, editingReview?.id);
      if (!editingReview) {
        setHasDraft(false);
      }

      setShowReviewDialog(false);
      setFormData({ rating: 5, content: '' });
      imageUpload.reset();
    } catch (error) {
      console.error('[reviews:submit] Error submitting review:', {
        isEditing: !!editingReview,
        reviewId: editingReview?.id,
        itemUin: editingReview?.itemUin ?? itemUin,
        error: error instanceof Error ? error.message : error,
      });
      toast.error(editingReview ? 'שגיאה בעדכון הביקורת' : 'שגיאה בהוספת הביקורת');
    } finally {
      setSubmitting(false);
    }
  };

  // Dialog close handlers
  const handleCloseDialog = (): void => {
    if (shouldClearDraftRef.current) {
      // Closed via cancel or submit - clear draft
      clearReviewDraft(itemUin, editingReview?.id);
      if (!editingReview) {
        setHasDraft(false);
      }
    } else {
      // Closed via ESC or backdrop - save draft
      saveReviewDraft(itemUin, formData.rating, formData.content, editingReview?.id);
      if (!editingReview) {
        setHasDraft(true);
      }
    }

    setShowReviewDialog(false);
    shouldClearDraftRef.current = false;
    imageUpload.reset();
  };

  const handleCancelDialog = (): void => {
    // Clear draft when cancel button is clicked
    shouldClearDraftRef.current = true;
    clearReviewDraft(itemUin, editingReview?.id);
    if (!editingReview) {
      setHasDraft(false);
    }
    setShowReviewDialog(false);
    shouldClearDraftRef.current = false;
    imageUpload.reset();
  };

  // Lightbox handlers
  const handleImageClick = (images: string[], index: number): void => {
    setCurrentImages(images);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxNext = (): void => {
    setCurrentImageIndex((prev) => (prev < currentImages.length - 1 ? prev + 1 : prev));
  };

  const handleLightboxPrev = (): void => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleItemClick = (review: Review & { itemName?: string }): void => {
    if (review.itemUin) {
      navigate({
        to: '/item/$uin/$name',
        params: {
          uin: review.itemUin.toString(),
          name: review.itemName || 'product',
        },
      });
    }
  };

  return (
    <div className="space-y-6">
      <ReviewHeader
        reviews={reviews}
        isAuthenticated={isAuthenticated}
        hasUserReviewed={hasUserReviewed()}
        hasDraft={hasDraft}
        onAddReview={handleAddReview}
      />

      {reviews.length === 0 ? (
        <ReviewsEmptyState
          itemName={itemName}
          isAuthenticated={isAuthenticated}
          hasUserReviewed={hasUserReviewed()}
          hasDraft={hasDraft}
          onAddReview={handleAddReview}
        />
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              canEdit={canEditReview(review)}
              showUserInfo={true}
              showItemName={false}
              onEdit={handleEditReview}
              onDelete={handleDeleteReview}
              onImageClick={handleImageClick}
              onItemClick={handleItemClick}
            />
          ))}
        </div>
      )}

      <ReviewForm
        open={showReviewDialog}
        isEditing={!!editingReview}
        itemName={itemName}
        submitting={submitting}
        formData={formData}
        errors={errors}
        uploadedImages={imageUpload.uploadedImages}
        uploadingFiles={imageUpload.uploadingFiles}
        previewUrls={imageUpload.previewUrls}
        isDragging={imageUpload.isDragging}
        progressMessages={imageUpload.progressMessages}
        onClose={handleCloseDialog}
        onCancel={handleCancelDialog}
        onSubmit={handleSubmitReview}
        onRatingChange={(rating) => setFormData((prev) => ({ ...prev, rating }))}
        onContentChange={(content) => setFormData((prev) => ({ ...prev, content }))}
        onFileSelect={imageUpload.handleFileSelect}
        onDragOver={imageUpload.handleDragOver}
        onDragLeave={imageUpload.handleDragLeave}
        onDrop={imageUpload.handleDrop}
        onDeleteUploadedImage={handleDeleteUploadedImage}
        onRemoveUploadingFile={imageUpload.handleRemoveUploadingFile}
        onRemoveProgressMessage={imageUpload.removeProgressMessage}
      />

      <ImageLightbox
        open={lightboxOpen}
        images={currentImages}
        currentIndex={currentImageIndex}
        onClose={() => setLightboxOpen(false)}
        onNext={handleLightboxNext}
        onPrev={handleLightboxPrev}
      />

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        deleting={deleting}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDeleteReview}
      />
    </div>
  );
}
